import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Input, Modal, Select, Spin } from "antd";
import { checkEmptyArray, checkEmptyObject, getWindowWidth, isSearchMobile } from "../../../../services";
import MultiSelectHeader from "../../MultiSelectModal/MultiSelectHeader";
import MultiSelectFooter from "../../MultiSelectModal/MultiSelectFooter";
import ReversedCheckbox from "../../ReversedCheckbox";
import styles from "./styles.scss";
import {
  HrCountryId,
  isCountryFilterId,
  LocationLevel,
  LocationSearchFieldByLevel,
  NoneHrCountryId
} from "../../../../models/enums";
import { useLocations, usePopularLocations } from "../../../../routes/Configuration/hooks";
import {
  CountryFilterLabels,
  getPopularLocationIds,
  locationLevelKey
} from "../../../../routes/Configuration/helpers";

// Option key of a location: "county:<id>", "city:<id>", ... (parsed back in handleChange)
const locationKey = (level: number, id: string) => `${locationLevelKey(level)}:${id}`;

// Entries shown before the user types: "Top lokacije" (counties / cities marked
// popular, in API order) and the two country filters, all from
// configuration/locations/popular.
const splitPopularLocations = (popularLocations) => {
  const top = [];
  const countryFilters = [];

  (popularLocations || []).forEach(location => {
    if (location.level === LocationLevel.country) {
      countryFilters.push({ key: locationKey(location.level, location.id), label: location.name });
    } else {
      top.push({ key: locationKey(location.level, location.id), label: location.name });
    }
  });

  // Until the API answers (or if it fails) the two filters are still offered.
  if (countryFilters.length === 0) {
    countryFilters.push({ key: `country:${HrCountryId}`, label: CountryFilterLabels[HrCountryId] });
    countryFilters.push({ key: `country:${NoneHrCountryId}`, label: CountryFilterLabels[NoneHrCountryId] });
  }

  return { top, countryFilters };
};

// Ids selected in the search query, with the level each query field stands for
const selectedLocationIds = selectedValues => {
  const ids = [];

  if (checkEmptyObject(selectedValues)) {
    LocationSearchFieldByLevel.forEach((field, level) => {
      if (selectedValues[field]) {
        selectedValues[field].forEach(id => ids.push({ id, level }));
      }
    });
  }

  return ids;
};

const { Option, OptGroup } = Select;
const DebounceSelect = props => {
  const {
    filterLocations,
    foundLocations,
    renderOptionText,
    onChange,
    selectedValues,
    fetching,
    placeholder,
    modalTitle,
    getPopupContainer,
    onActiveChange
  } = props;

  const popularLocations = usePopularLocations();
  const popularIds = useMemo(() => getPopularLocationIds(popularLocations), [popularLocations]);
  const { top: topLocations, countryFilters } = useMemo(
    () => splitPopularLocations(popularLocations),
    [popularLocations]
  );

  // Names of the ids already selected (e.g. from the url) that are not among
  // the popular ones come from configuration/locations/:id.
  const selectedIds = useMemo(() => selectedLocationIds(selectedValues), [selectedValues]);
  const knownLocations = useLocations(
    selectedIds.filter(({ id }) => !isCountryFilterId(id)).map(({ id }) => id)
  );

  const [value, setValue] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isShowModal, setIsShowModal] = useState(false);
  const [pendingValues, setPendingValues] = useState([]);
  // Locations listed under "Odabrano": the selection at open plus anything checked
  // since. Unchecking keeps the item listed so it can be checked again.
  const [shownSelectedValues, setShownSelectedValues] = useState([]);
  const [isShownDropdown, setIsShownDropdown] = useState(false);
  const selectRef = useRef(null);

  // Pause the animated placeholder (~40-80ms tick) while the dropdown/modal
  // is open — its setState re-renders the Select and breaks the inner scroll
  // even with defaultActiveFirstOption disabled.
  useEffect(() => {
    if (onActiveChange) onActiveChange(isShownDropdown || isShowModal);
  }, [isShownDropdown, isShowModal]);

  // Label of an already selected id, or undefined while it is still being fetched
  const labelOf = (id: string) => {
    if (CountryFilterLabels[id]) {
      return CountryFilterLabels[id];
    }

    const popular = (popularLocations || []).find(location => location.id === id);
    if (popular) {
      return popular.name;
    }

    const known = knownLocations[id];
    if (known === undefined) {
      return undefined;
    }

    // unknown id (404): keep the id so the filter stays visible and removable
    return known ? known.name : id;
  };

  useEffect(() => {
    if (!checkEmptyArray(value) && selectedIds.length > 0) {
      const locationsData = selectedIds.map(({ id, level }) => ({
        key: locationKey(level, id),
        label: labelOf(id)
      }));

      // wait until every selected location has a name
      if (locationsData.every(location => location.label !== undefined)) {
        setValue(locationsData);
      }
    }
  }, [selectedValues, knownLocations, popularLocations]);

  const handleChange = values => {
    let country = [];
    let county = [];
    let city = [];
    let settlement = [];

    values.forEach(el => {
      let temp = el.key.split(":");
      if (temp[0].includes("country")) {
        country.push(temp[1]);
      } else if (temp[0].includes("county")) {
        county.push(temp[1]);
      } else if (temp[0].includes("city")) {
        city.push(temp[1]);
      } else if (temp[0].includes("settlement")) {
        settlement.push(temp[1]);
      }
    });

    let response = {};
    // country
    if (checkEmptyArray(country)) {
      response = { ...response, includeCountryIds: country };
    }

    if (checkEmptyArray(county)) {
      response = { ...response, includeCountyIds: county };
    }

    if (checkEmptyArray(city)) {
      response = { ...response, includeCityIds: city };
    }

    if (checkEmptyArray(settlement)) {
      response = { ...response, includeSettlementIds: settlement };
    }

    setValue(values);

    const result = {
      ["includeCountryIds"]: undefined,
      ["includeCountyIds"]: undefined,
      ["includeCityIds"]: undefined,
      ["includeSettlementIds"]: undefined,
      ...response
    };

    onChange(result);
  };

  const handleFilter = e => {
    setIsDirty(e.length > 0);
    filterLocations(e);
  };

  const generateIds = object => (object ? locationKey(object.level, object.id) : undefined);

  // Counties / cities from configuration/locations/popular are listed under
  // "Top lokacije" in the results; settlements never are.
  const isPopular = d =>
    d.level !== LocationLevel.settlement && d.level !== LocationLevel.country && popularIds.has(d.id);

  const onSelectBlur = () => {
    filterLocations("  "); // filter only trigger when input is greater or equal to 2
  };

  const onDeselectHandle = e => {
    let newLocations = value.filter(location => location.key !== e.key);
    setValue(newLocations);
    handleChange(newLocations);
  };

  const handleModalItemToggle = (key, label) => {
    const exists = pendingValues.some(v => v.key === key);
    if (exists) {
      setPendingValues(pendingValues.filter(v => v.key !== key));
    } else {
      setPendingValues([...pendingValues, { key, label }]);
      // Don't push into shownSelectedValues here — the user would see the
      // location they just picked immediately duplicated in "Odabrano"
      // above the list, which looks visually noisy. "Odabrano" is a
      // snapshot of the applied selection taken at modal open; new picks
      // show up there the next time the modal is opened.
    }
  };

  const handleModalOpen = () => {
    setPendingValues([...value]);
    setShownSelectedValues([...value]);
    setIsShowModal(true);
  };

  const closeModal = () => {
    setIsShowModal(false);
    setIsDirty(false);
    filterLocations("  ");
    document.body.style.overflow = "";
    document.body.style.position = "";
  };

  const handleModalCancel = () => {
    closeModal();
  };

  const handleModalOk = () => {
    handleChange(pendingValues);
    closeModal();
  };

  if (isSearchMobile(getWindowWidth())) {
    var popularResults = isDirty ? foundLocations.filter(isPopular) : [];
    var otherResults = isDirty ? foundLocations.filter(function (d) { return !isPopular(d); }) : foundLocations;
    // "Odabrano" is shown only while the search input is empty
    var showSelected = shownSelectedValues.length > 0 && !isDirty;

    return (
      <React.Fragment>
        {isShowModal && <Modal
          closable={false}
          footer={null}
          visible={true}
          onCancel={handleModalCancel}
          style={{ paddingBottom: 0, paddingTop: 0, marginBottom: 0, marginTop: 0, top: 0 }}
          bodyStyle={{ paddingBottom: 20, paddingTop: 12 }}
        >
          <MultiSelectHeader title={modalTitle || "Lokacija"} onCancel={handleModalCancel} />
          <Input
            placeholder={placeholder}
            onChange={e => handleFilter(e.target.value)}
            autoFocus
          />
          <div style={{ maxHeight: 150, overflowY: "auto", marginTop: 10 }}>
            {fetching ? (
              <div style={{ textAlign: "center", padding: 20, color: "#999" }}>
                <Spin size="small" style={{ marginRight: 8 }} />
                {"Učitavam lokacije..."}
              </div>
            ) : (
              <React.Fragment>
                {showSelected && (
                  <div className={styles.selectedGroup}>
                    <div className={styles.groupLabel}>{"Odabrano"}</div>
                    {shownSelectedValues.map(v => (
                      <div key={v.key} className={styles.modalItem}>
                        <ReversedCheckbox
                          label={v.label}
                          checked={pendingValues.some(p => p.key === v.key)}
                          onChange={() => handleModalItemToggle(v.key, v.label)}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {!isDirty && topLocations.length > 0 && (
                  <React.Fragment>
                    <div className={styles.groupLabel}>{"Top lokacije"}</div>
                    {topLocations.map(function (loc) {
                      return (
                        <div key={loc.key} className={styles.modalItem}>
                          <ReversedCheckbox
                            label={loc.label}
                            checked={pendingValues.some(v => v.key === loc.key)}
                            onChange={() => handleModalItemToggle(loc.key, loc.label)}
                          />
                        </div>
                      );
                    })}
                  </React.Fragment>
                )}
                {!isDirty && (
                  <React.Fragment>
                    {countryFilters.map(function (loc) {
                      return (
                        <div key={loc.key} className={styles.modalItem}>
                          <ReversedCheckbox
                            label={loc.label}
                            checked={pendingValues.some(v => v.key === loc.key)}
                            onChange={() => handleModalItemToggle(loc.key, loc.label)}
                          />
                        </div>
                      );
                    })}
                  </React.Fragment>
                )}
                <React.Fragment>
                  {popularResults.length > 0 && (
                    <React.Fragment>
                      <div className={styles.groupLabel}>{"Top lokacije"}</div>
                      {popularResults.map(function (d, i) {
                        var key = String(generateIds(d) || "p" + i);
                        var label = renderOptionText(d);
                        return (
                          <div key={key} className={styles.modalItem}>
                            <ReversedCheckbox
                              label={label}
                              checked={pendingValues.some(v => v.key === key)}
                              onChange={() => handleModalItemToggle(key, label)}
                            />
                          </div>
                        );
                      })}
                      <div className={styles.groupLabel}>{"Ostale lokacije"}</div>
                    </React.Fragment>
                  )}
                  {otherResults.map(function (d, i) {
                    var key = String(generateIds(d) || "o" + i);
                    var label = renderOptionText(d);
                    return (
                      <div key={key} className={styles.modalItem}>
                        <ReversedCheckbox
                          label={label}
                          checked={pendingValues.some(v => v.key === key)}
                          onChange={() => handleModalItemToggle(key, label)}
                        />
                      </div>
                    );
                  })}
                </React.Fragment>
                {isDirty && foundLocations.length === 0 && (
                  <div style={{ padding: "10px 0", color: "#999" }}>
                    {"Nema željene lokacije"}
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
          <MultiSelectFooter onCancel={handleModalCancel} onOk={handleModalOk} />
        </Modal>}
        <div
          role="button"
          tabIndex={-1}
          onClick={handleModalOpen}
          style={{ position: "relative" }}
        >
          <div style={{ position: "absolute", inset: 0, zIndex: 1 }} />
          <Select
            disabled
            className={styles.mobileContent}
            value={value.length > 0 ? value.map(v => v.label).join(", ") : undefined}
            placeholder={placeholder}
          />
        </div>
      </React.Fragment>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {!isShownDropdown && (
        <div
          onClick={() => {
            setIsShownDropdown(true);
            setTimeout(() => { selectRef.current && selectRef.current.focus(); }, 0);
          }}
          style={{ position: "absolute", inset: 0, zIndex: 1, cursor: "text" }}
        >
          <Select
            disabled
            className={styles.mobileContent}
            value={value.length > 0 ? value.map(v => v.label).join(", ") : undefined}
            placeholder={placeholder}
            style={{ width: "100%", pointerEvents: "none" }}
          />
        </div>
      )}
      <Select
        mode={"multiple"}
        autoClearSearchValue={false}
        labelInValue
        defaultActiveFirstOption={false}
        open={isShownDropdown}
        value={value}
        placeholder={placeholder}
        filterOption={false}
        onSearch={handleFilter}
        onChange={e => handleChange(e)}
        ref={selectRef}
        onBlur={() => { setIsShownDropdown(false); setIsDirty(false); onSelectBlur(); }}
        className={styles.locationFilter}
        style={!isShownDropdown ? { opacity: 0, height: 0, overflow: "hidden" } : undefined}
        getPopupContainer={getPopupContainer}
        notFoundContent={
          fetching ? (
            <span>
              <Spin size="small" style={{ marginRight: 8 }} />
              {"Učitavam lokacije..."}
            </span>
          ) : (
            "Nema željene lokacije"
          )
        }
        onDeselect={onDeselectHandle}
      >
        {!isDirty && topLocations.length > 0 && (
          <OptGroup key="top" label="Top lokacije">
            {topLocations.map(function (loc) {
              return <Option key={loc.key}>{loc.label}</Option>;
            })}
            {countryFilters.map(function (loc) {
              return <Option key={loc.key}>{loc.label}</Option>;
            })}
          </OptGroup>
        )}
        {!isDirty && !topLocations.length && countryFilters.map(function (loc) {
          return <Option key={loc.key}>{loc.label}</Option>;
        })}
        {(() => {
          var popularResults = foundLocations.filter(isPopular);
          var otherResults = foundLocations.filter(function (d) { return !isPopular(d); });

          if (isDirty && popularResults.length > 0) {
            return [
              <OptGroup key="top-search" label="Top lokacije">
                {popularResults.map(function (d, i) {
                  return <Option key={generateIds(d) || "p" + i}>{renderOptionText(d)}</Option>;
                })}
              </OptGroup>,
              <OptGroup key="other-search" label="Ostale lokacije">
                {otherResults.map(function (d, i) {
                  return <Option key={generateIds(d) || "o" + i}>{renderOptionText(d)}</Option>;
                })}
              </OptGroup>
            ];
          }
          return foundLocations.map(function (d, i) {
            return <Option key={generateIds(d) || i}>{renderOptionText(d)}</Option>;
          });
        })()}
      </Select>
    </div>
  );
};

export default DebounceSelect;



// WEBPACK FOOTER //
// ./src/components/FormControls/SearchInputLocation/DebounceSelect/DebounceSelect.tsx